import { notFound } from "@tanstack/react-router";
import { isApiError } from "@majourneys/api-client";

/** Long enough for a blip to pass, short enough not to stall the SSR render. */
const RETRY_DELAY_MS = 250;

/**
 * Worth trying a second time? Only failures that carry no verdict about the
 * request itself: a dropped connection (`status: 0`, no HTTP response), a
 * timeout, a rate limit, or the API being briefly unwell.
 *
 * Everything else is deterministic — a 400 or a 403 will say the same thing
 * the second time, and retrying a programming error only delays it by
 * `RETRY_DELAY_MS` and doubles the work.
 */
function isTransient(error: unknown): boolean {
  if (!isApiError(error)) return false;
  return error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Run a route loader / beforeLoad body, translating API 404 responses into a
 * TanStack Router `notFound()` so SSR returns a real HTTP 404 (instead of
 * 200 + a NotFound page, which is a soft-404 for search engines), and
 * retrying once when the API fails in a way that says nothing about the
 * request.
 *
 * The retry exists because this helper is the single point where a page
 * decides it cannot be rendered. It used to rethrow everything that was not a
 * 404, so one bad second from `api.majourneys.com` — a 502, a timeout, a
 * dropped connection — put the full-page "Our side hit a snag" screen in
 * front of a guest, on a page that worked perfectly a moment before and a
 * moment after. That is what happened on 2026-09-11: a booking was submitted
 * successfully and the very next render of the activity page 500'd, once,
 * unreproducibly. A single retry costs 250ms in the rare bad case and saves
 * the visit.
 *
 * Usage:
 *   loader: async ({ params, context }) =>
 *     withApi404(async () => {
 *       await context.queryClient.ensureQueryData({ ... });
 *     });
 */
export async function withApi404<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (isApiError(error) && error.status === 404) throw notFound();
    if (!isTransient(error)) throw error;

    await sleep(RETRY_DELAY_MS);
    try {
      return await fn();
    } catch (retryError) {
      // One retry, not a loop: a struggling API should not be hammered by
      // every SSR render, and the guest should see the error page rather than
      // a page that hangs.
      if (isApiError(retryError) && retryError.status === 404) throw notFound();
      throw retryError;
    }
  }
}
